import {createSlice, createAsyncThunk, Draft, PayloadAction} from '@reduxjs/toolkit';
import axios from 'axios';
import {
  GroupDeploymentDetailsRequest,
  getDeploymentDetails,
  getDeviceGroupDeploymentDetails,
} from '../../api/deployment/DeploymentDetails';
import URL from '../../config/url';
import {DeploymentDashboardRequest} from '../../types/deployment/DeploymentDashboardRequest';
import {
  DeploymentDashboardInfo,
  DeploymentDashboardResponse,
} from '../../types/deployment/DeploymentDashboardResponse';
import {DeploymentDetailsInfo} from '../../types/deployment/DeploymentDetailsInfo';
import {
  Deployment,
  DeploymentResponse,
  DeploymentStatusType,
} from '../../types/deployment/DeploymentResponse';
import {InferenceApplicationsRequest} from '../../types/deployment/InferenceApplicationsRequest';
import {
  InferenceApplicationInfo,
  InferenceApplicationsResponse,
} from '../../types/deployment/InferenceApplicationsResponse';
import {StartInferenceRequest} from '../../types/deployment/InferenceRequest';
import {deleteSinglePageApplication} from '../../api/application/DeleteSpa';
import {getDeploymentStatus} from '../../api/deployment/DeploymentStatus';
import {toast} from '../../base-components/StudioToast';
import {
  AddOrUpdateDeviceGroup,
  DeleteDeviceGroupResponse,
  DeviceGroupInfo,
  DeviceGroupNode,
  DeviceGroupResponse,
  DeviceGroupsRequest,
  DeviceGroupsResponse,
  UpdateDeviceGroupResponse,
} from '../../types/deployment/DeploymentDeviceGroup';
import {downloadBlob} from '../../utils';
import {
  SinglePageApplication,
  SinglePageApplicationListResponse,
} from '../../types/project/Application';

type DeploymentDashboardState = {
  details: DeploymentDetailsInfo | null;
  selectedNodeDeploymentId: string | null;
  selectedDeployment: DeploymentDetailsInfo | null;
  deviceGroupDeploymentDetails: DeploymentDetailsInfo[];
  deployments: {
    data: Array<DeploymentDashboardInfo>;
    count: number;
  };
  inferenceApps: {
    data: Array<InferenceApplicationInfo>;
    count: number;
  };
  deviceGroups: {
    data: Array<DeviceGroupInfo>;
    count: number;
  };
  recentDeployments: {
    data: Array<DeploymentDashboardInfo>;
    count: number;
  };
  /* 
      The /start and /stop requests are synchronous with high latency. To give the user quick feedback, we will
      optimistically upate the status to "STARTING" or "STOPPING" when the request is initiated. 
      This map here stores the optimistic updates, which will be removed when the request completes.
    */
  optimisticallyUpdatedStatuses: Record<string, DeploymentStatusType | null>;
  applicationSpaLists: SinglePageApplication[];
};

export const fetchInferenceAppsSync = async (request: InferenceApplicationsRequest) => {
  const {data} = await axios.post<InferenceApplicationsResponse>(
    URL.INFERENCE_APPLICATIONS,
    request,
    {
      headers: {
        bypassDefaultErrorHandler: true,
      },
    }
  );
  if (data.errors?.length) {
    toast.error(data.errors[0].message);
  }
  return data.body;
};

export const fetchInferenceApps = createAsyncThunk(
  'prod-deployment-dashboard/fetchInferenceApps',
  fetchInferenceAppsSync
);

export const fetchDeployments = createAsyncThunk(
  'prod-deployment-dashboard/fetchDeployments',
  async (request: DeploymentDashboardRequest) => {
    const {data} = await axios.post<DeploymentDashboardResponse>(
      URL.DEPLOYMENT_DASHBOARD,
      request
    );
    return data.body;
  }
);

export const fetchRecentDeployments = createAsyncThunk(
  'prod-deployment-dashboard/fetchRecentDeployments',
  async (request: DeploymentDashboardRequest) => {
    const {data} = await axios.post<DeploymentDashboardResponse>(
      URL.DEPLOYMENT_DASHBOARD,
      {...request, params: {...request.params, status: ['RUNNING', 'IDLE', 'STOPPING']}}
    );
    return data.body;
  }
);

export const fetchDeploymentDetails = createAsyncThunk(
  'prod-deployment-dashboard/fetchDeploymentDetails',
  async (id: string) => {
    const data = await getDeploymentDetails({deploymentId: id});
    if (data.errors?.length) {
      throw new Error('Error during details fetch');
    }
    return data.body;
  }
);

export const fetchDeviceGroupDeploymentDetails = createAsyncThunk(
  'prod-deployment-dashboard/fetchDeviceGroupDeploymentDetails',
  async (params: GroupDeploymentDetailsRequest) => {
    const data = await getDeviceGroupDeploymentDetails(params);
    if (data.errors?.length) {
      throw new Error('Error during details fetch');
    }
    return data.body;
  }
);

export async function fetchDeploymentStatusStateless(id: string) {
  const data = await getDeploymentStatus({deploymentId: id});
  if (data.errors?.length) {
    throw new Error('Error during details fetch');
  }
  return data.body;
}

export const startDeployment = createAsyncThunk(
  'prod-deployment-dashboard/start',
  async (values: StartInferenceRequest) => {
    return startDeploymentStateless(values);
  }
);

export async function startDeploymentStateless(values: StartInferenceRequest) {
  const {data} = await axios.post<DeploymentResponse>(URL.START_DEPLOYMENT, values, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (data.errors?.length) {
    throw new Error('Error during start deployment');
  }
  return data.body;
}

export async function stopDeploymentStateless(id: string) {
  const {data} = await axios.post<DeploymentResponse>(URL.STOP_DEPLOYMENT, null, {
    params: {
      deploymentId: id,
    },
  });
  if (data.errors?.length) {
    throw new Error('Error during stop deployment');
  }
  return data.body;
}

export const restartDeployment = createAsyncThunk(
  'prod-deployment-dashboard/restart',
  async (deploymentId: string) => {
    const {data} = await axios.post<DeploymentResponse>(
      URL.RESTART_DEPLOYMENT,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          deploymentId: deploymentId,
        },
      }
    );
    if (data.errors?.length) {
      throw new Error('Error during restart deployment');
    }
    return data.body;
  }
);

export const stopDeployment = createAsyncThunk(
  'prod-deployment-dashboard/stop',
  async (id: string) => {
    const {data} = await axios.post<DeploymentResponse>(URL.STOP_DEPLOYMENT, null, {
      params: {
        deploymentId: id,
      },
    });
    if (data.errors?.length) {
      throw new Error('Error during stop deployment');
    }
    return data.body;
  }
);

export const deleteDeployment = createAsyncThunk(
  'prod-deployment-dashboard/delete',
  async (id: string) => {
    const {data} = await axios.delete<DeploymentResponse>(URL.DELETE_DEPLOYMENT, {
      params: {
        deploymentId: id,
      },
    });
    return data.body;
  }
);

export const deleteInferenceApp = createAsyncThunk(
  'prod-deployment-dashboard/delete-inference-app',
  async ({applicationId, spaId}: {applicationId: string; spaId?: string}) => {
    try {
      if (!spaId) {
        return;
      }
      await deleteSinglePageApplication(applicationId, spaId);
    } catch (e) {
      console.error(e);
    }
  }
);

export const fetchApplicationSpaList = createAsyncThunk(
  'prod-deployment-dashboard/fetchApplicationSpaList',
  async (request: {applicationId: string; targetType: string}) => {
    const {data} = await axios.get<SinglePageApplicationListResponse>(
      URL.APPLICATION_SPA_LIST(request.applicationId, request.targetType)
    );
    if (data && data.errors && data.errors?.length > 0) {
      toast.error(data.errors);
      return [];
    }
    return data.body;
  }
);

export const fetchDeviceGroups = createAsyncThunk(
  'prod-deployment-dashboard/fetchDeviceGroups',
  async (request: DeviceGroupsRequest) => {
    const {data} = await axios.post<DeviceGroupsResponse>(URL.DEVICE_GROUPS, request);
    return data.body;
  }
);

export const addDeviceGroup = createAsyncThunk(
  'prod-deployment-dashboard/addDeviceGroup',
  async ({
    request,
    nodes,
  }: {
    request: AddOrUpdateDeviceGroup;
    nodes: DeviceGroupNode[];
  }) => {
    const {data} = await axios.post<UpdateDeviceGroupResponse>(
      URL.UPDATE_DEVICE_GROUP,
      request
    );
    const newDevicegroup: DeviceGroupInfo = {
      ...data.body,
      nodes: ((data.body?.nodes as unknown) as string[])?.map(node => {
        return {
          id: node,
          name: nodes.find(n => n.id === node)?.name,
          status: 'ONLINE',
        } as DeviceGroupNode;
      }),
    };
    return newDevicegroup;
  }
);

export const updateDeviceGroup = createAsyncThunk(
  'prod-deployment-dashboard/updateDeviceGroup',
  async (request: AddOrUpdateDeviceGroup) => {
    const {data} = await axios.post<UpdateDeviceGroupResponse>(
      URL.UPDATE_DEVICE_GROUP,
      request
    );
    return data.body;
  }
);

export const deleteDeviceGroup = createAsyncThunk(
  'prod-deployment-dashboard/delete-device-group',
  async (ids: string[]) => {
    await axios.post<DeleteDeviceGroupResponse>(URL.DELETE_DEVICE_GROUP, ids);
    return ids;
  }
);

export const uploadDeviceGroup = createAsyncThunk(
  'prod-deployment-dashboard/upload-device-group',
  async (files: File[]) => {
    const formData = new FormData();
    files.forEach(file => formData.append('file', file, file?.name));

    const {data} = await axios.post<DeviceGroupResponse<{}>>(
      URL.UPLOAD_DEVICE_GROUP,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (data.errors) {
      data.errors.forEach(error => toast.error(error.detailMessage));
      return;
    }

    return data.body;
  }
);

export const downloadDeviceGroups = createAsyncThunk(
  'prod-deployment-dashboard/download-device-groups',
  async (ids: string[]) => {
    const {data} = await axios.post(URL.DOWNLOAD_DEVICE_GROUP, ids, {
      responseType: 'blob',
    });
    const urlCreator = window.URL || window.webkitURL;
    const fileUrl = urlCreator.createObjectURL(data);

    const {promise} = downloadBlob({fileName: 'group-details', blobUrl: fileUrl});
    await promise;
    return data.body;
  }
);

const updateStatusHelper = (
  updatedDeployment: Deployment,
  state: Draft<DeploymentDashboardState>
) => {
  if (updatedDeployment.status) {
    // update status in deployments list
    const matchedDeployment = state.deployments.data.find(
      deployment => deployment.id === updatedDeployment.id
    );
    if (matchedDeployment && updatedDeployment.status) {
      matchedDeployment.status = updatedDeployment.status;
    }

    // update status and video url in deployment details
    if (state.selectedDeployment?.id === updatedDeployment.id) {
      if (state.selectedDeployment) {
        state.selectedDeployment.status = updatedDeployment.status;
        state.selectedDeployment.videoOutputRtspUrl =
          updatedDeployment.deploymentOutput.videoOutputRtspUrl;
      }
    }
  }

  // rollback optimistic update
  state.optimisticallyUpdatedStatuses[updatedDeployment.id] = null;
};

const initialState: DeploymentDashboardState = {
  details: null,
  selectedDeployment: null,
  selectedNodeDeploymentId: null,
  deviceGroupDeploymentDetails: [],
  deployments: {
    data: [],
    count: 0,
  },
  recentDeployments: {
    data: [],
    count: 0,
  },
  inferenceApps: {
    data: [],
    count: 0,
  },
  deviceGroups: {
    data: [],
    count: 0,
  },
  optimisticallyUpdatedStatuses: {},
  applicationSpaLists: [],
};

const deploymentDashboardSlice = createSlice({
  name: 'prod-deployment-dashboard',
  initialState,
  reducers: {
    setSelectedDeployment: (
      state,
      action: PayloadAction<DeploymentDetailsInfo | null>
    ) => {
      state.selectedNodeDeploymentId = action.payload ? action.payload.id : null;
      state.selectedDeployment = action.payload;
    },
    setDeviceGroupDeploymentDetails: (
      state,
      action: PayloadAction<DeploymentDetailsInfo[]>
    ) => {
      state.deviceGroupDeploymentDetails = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDeployments.fulfilled, (state, action) => {
        const {count, entries} = action.payload;
        state.deployments.count = count;
        state.deployments.data = entries;
      })
      .addCase(fetchRecentDeployments.fulfilled, (state, action) => {
        const {count, entries} = action.payload;
        state.recentDeployments.count = count;
        state.recentDeployments.data = entries;
      })
      .addCase(fetchDeploymentDetails.fulfilled, (state, action) => {
        state.details = action.payload;
        state.selectedDeployment = action.payload;
      })
      .addCase(fetchDeviceGroupDeploymentDetails.fulfilled, (state, action) => {
        state.deviceGroupDeploymentDetails = action.payload;
      })
      .addCase(startDeployment.pending, (state, action) => {
        const values = action.meta.arg;
        state.optimisticallyUpdatedStatuses[values.deploymentId] = 'STARTING';
      })
      .addCase(startDeployment.rejected, (state, action) => {
        const values = action.meta.arg;
        state.optimisticallyUpdatedStatuses[values.deploymentId] = null;
      })
      .addCase(startDeployment.fulfilled, (state, action) => {
        updateStatusHelper(action.payload, state);
      })
      .addCase(stopDeployment.pending, (state, action) => {
        const deploymentId = action.meta.arg;
        state.optimisticallyUpdatedStatuses[deploymentId] = 'STOPPING';
      })
      .addCase(stopDeployment.rejected, (state, action) => {
        const deploymentId = action.meta.arg;
        state.optimisticallyUpdatedStatuses[deploymentId] = null;
      })
      .addCase(stopDeployment.fulfilled, (state, action) => {
        updateStatusHelper(action.payload, state);
      })
      .addCase(deleteDeployment.fulfilled, (state, action) => {
        const deletedDeployment = action.payload;
        const updatedDeploymentsList = state.deployments.data.filter(
          deployment => deployment.id !== deletedDeployment.id
        );
        state.deployments.data = updatedDeploymentsList;
      })
      .addCase(fetchApplicationSpaList.fulfilled, (state, action) => {
        state.applicationSpaLists = action.payload;
      })
      .addCase(deleteInferenceApp.fulfilled, (state, action) => {
        const deletedInferenceAppId = action.meta.arg.spaId;
        state.inferenceApps.data = state.inferenceApps.data.filter(
          app => app.id !== deletedInferenceAppId
        );
        state.applicationSpaLists = state.applicationSpaLists.filter(
          app => app.id !== deletedInferenceAppId
        );
      })
      .addCase(fetchInferenceApps.fulfilled, (state, action) => {
        if (action.payload) {
          const {count, entries} = action.payload;
          state.inferenceApps.count = count;
          state.inferenceApps.data = entries;
        }
      })
      .addCase(fetchDeviceGroups.fulfilled, (state, action) => {
        const {count, entries} = action.payload;
        state.deviceGroups.count = count;
        state.deviceGroups.data = entries;
      })
      .addCase(deleteDeviceGroup.fulfilled, (state, action) => {
        const deletedDeviceGroups = action.payload;
        state.deviceGroups.data = state.deviceGroups.data.filter(
          deviceGroup => !deletedDeviceGroups.includes(deviceGroup.id)
        );
      })
      .addCase(updateDeviceGroup.fulfilled, (state, action) => {
        const updatedDeviceGroup = action.payload;
        const updatedData = state.deviceGroups.data.map(deviceGroup => {
          if (deviceGroup.id === updatedDeviceGroup.id) {
            return updateDeviceGroup;
          }
          return deviceGroup;
        }) as DeviceGroupInfo[];
        state.deviceGroups.data = updatedData;
      })
      .addCase(addDeviceGroup.fulfilled, (state, action) => {
        const newData = [...state.deviceGroups.data, action.payload];
        state.deviceGroups.data = newData;
      });
  },
});

export const {reducer: deploymentDashboardReducer, actions} = deploymentDashboardSlice;
